// Период отчёта — один для всех страниц (Лиды, Офферы, Реклама, Финансы, Креативы) и для api.
// Дни — календарные по Ташкенту (UTC+5, без перехода на летнее время), границы включительно.
// Ключ периода живёт в адресе запроса: today | yesterday | 7d | 14d | 30d | all | month | prev_month
// или диапазон «2026-09-01..2026-09-15».

export const PERIOD_PRESETS = [
  { value: 'today', label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: '7d', label: '7 дней' },
  { value: '14d', label: '14 дней' },
  { value: '30d', label: '30 дней' },
  { value: 'all', label: 'Всё время' },
] as const;

/** Месяцы — для отчётов, где считают деньги помесячно (Финансы). */
export const MONTH_PRESETS = [
  { value: 'month', label: 'Этот месяц' },
  { value: 'prev_month', label: 'Прошлый месяц' },
] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number]['value'] | (typeof MONTH_PRESETS)[number]['value'];

export type Period =
  | { kind: 'preset'; preset: PeriodPreset }
  | { kind: 'range'; since: string; until: string };

const PRESETS = new Set<string>([...PERIOD_PRESETS, ...MONTH_PRESETS].map((p) => p.value));
/** Старые ключи страниц (days=7, period=30) — чтобы старые ссылки и закладки не ломались. */
const LEGACY: Record<string, PeriodPreset> = {
  '1': 'today',
  '7': '7d',
  '14': '14d',
  '30': '30d',
  '90': 'all',
};
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** Диапазон — не длиннее двух лет: иначе запросы к статистике становятся тяжёлыми. */
export const MAX_RANGE_DAYS = 731;
const DAY_MS = 86_400_000;
const TZ_OFFSET_MS = 5 * 3_600_000;

/** День по Ташкенту (ГГГГ-ММ-ДД). */
export function tashkentDay(d: Date = new Date()): string {
  return new Date(d.getTime() + TZ_OFFSET_MS).toISOString().slice(0, 10);
}

/** Сдвиг дня на n дней. */
export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

const validDay = (s: string) =>
  DAY.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && addDays(s, 0) === s;

/** Ключ из адреса → период. Непонятный ключ — fallback (страница не падает от кривой ссылки). */
export function parsePeriod(key: string | null | undefined, fallback: PeriodPreset = '30d'): Period {
  const k = (key ?? '').trim();
  if (PRESETS.has(k)) return { kind: 'preset', preset: k as PeriodPreset };
  if (LEGACY[k]) return { kind: 'preset', preset: LEGACY[k] };
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(k);
  if (m) {
    const [, a = '', b = ''] = m;
    if (validDay(a) && validDay(b)) {
      const [since, until] = a <= b ? [a, b] : [b, a];
      if (Date.parse(until) - Date.parse(since) <= (MAX_RANGE_DAYS - 1) * DAY_MS)
        return { kind: 'range', since, until };
    }
  }
  return { kind: 'preset', preset: fallback };
}

export function periodKey(p: Period): string {
  return p.kind === 'preset' ? p.preset : `${p.since}..${p.until}`;
}

/** Дни периода по Ташкенту включительно; since = null — «всё время» (с самого начала данных). */
export function periodRange(p: Period, now: Date = new Date()): { since: string | null; until: string } {
  const today = tashkentDay(now);
  if (p.kind === 'range') return { since: p.since, until: p.until };
  switch (p.preset) {
    case 'today':
      return { since: today, until: today };
    case 'yesterday':
      return { since: addDays(today, -1), until: addDays(today, -1) };
    case '7d':
      return { since: addDays(today, -6), until: today };
    case '14d':
      return { since: addDays(today, -13), until: today };
    case '30d':
      return { since: addDays(today, -29), until: today };
    case 'month':
      return { since: `${today.slice(0, 7)}-01`, until: today };
    case 'prev_month': {
      const first = `${today.slice(0, 7)}-01`;
      const last = addDays(first, -1);
      return { since: `${last.slice(0, 7)}-01`, until: last };
    }
    default:
      return { since: null, until: today };
  }
}

/** Начало дня по Ташкенту как момент времени. */
export function dayStart(day: string): Date {
  return new Date(Date.parse(`${day}T00:00:00Z`) - TZ_OFFSET_MS);
}

/** Границы по времени [from, to) — для запросов по created_at; from = null — без нижней границы. */
export function periodBounds(p: Period, now: Date = new Date()): { from: Date | null; to: Date } {
  const { since, until } = periodRange(p, now);
  return { from: since ? dayStart(since) : null, to: dayStart(addDays(until, 1)) };
}

/** Сколько дней в периоде (для «всё время» — null). */
export function periodLength(p: Period, now: Date = new Date()): number | null {
  const { since, until } = periodRange(p, now);
  return since ? Math.round((Date.parse(until) - Date.parse(since)) / DAY_MS) + 1 : null;
}

/**
 * Прошлый период той же длины — для сравнения «к прошлым 7 дням». У «сегодня» — вчера (целиком);
 * у «этого месяца» — те же дни прошлого месяца (26 дней сентября — к 1–26 августа, а не ко всему августу:
 * иначе неполный месяц всегда «хуже»; в коротком месяце — до его конца); у прошлого месяца — позапрошлый
 * целиком; у «всего времени» сравнивать не с чем.
 */
export function previousRange(p: Period, now: Date = new Date()): { since: string; until: string } | null {
  if (p.kind === 'preset' && p.preset === 'month') {
    const { since, until } = periodRange(p, now) as { since: string; until: string };
    const prev = periodRange({ kind: 'preset', preset: 'prev_month' }, now) as {
      since: string;
      until: string;
    };
    const sameDay = addDays(prev.since, Number(until.slice(8)) - Number(since.slice(8)));
    return { since: prev.since, until: sameDay < prev.until ? sameDay : prev.until };
  }
  if (p.kind === 'preset' && p.preset === 'prev_month') {
    const { since } = periodRange(p, now);
    const last = addDays(since as string, -1);
    return { since: `${last.slice(0, 7)}-01`, until: last };
  }
  const n = periodLength(p, now);
  const { since } = periodRange(p, now);
  if (!n || !since) return null;
  return { since: addDays(since, -n), until: addDays(since, -1) };
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** «5 сен» (год — если не текущий). */
export function dayLabel(day: string, now: Date = new Date()): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const year = y !== Number(tashkentDay(now).slice(0, 4)) ? ` ${y}` : '';
  return `${d} ${MONTHS[m - 1]}${year}`;
}

/** Подпись периода для кнопки и заголовков: «7 дней», «5–12 сен», «28 авг — 3 сен». */
export function periodLabel(p: Period, now: Date = new Date()): string {
  if (p.kind === 'preset')
    return [...PERIOD_PRESETS, ...MONTH_PRESETS].find((x) => x.value === p.preset)?.label ?? p.preset;
  if (p.since === p.until) return dayLabel(p.since, now);
  const [ys, ms] = p.since.split('-');
  const [yu, mu] = p.until.split('-');
  if (ys === yu && ms === mu) return `${Number(p.since.slice(8))}–${dayLabel(p.until, now)}`;
  return `${dayLabel(p.since, now)} — ${dayLabel(p.until, now)}`;
}

/** «за 7 дней», «за сегодня», «за 5–12 сен» — для подписей итогов. */
export function periodPhrase(p: Period, now: Date = new Date()): string {
  if (p.kind === 'preset') {
    if (p.preset === 'all') return 'за всё время';
    if (p.preset === 'month') return 'за этот месяц';
    if (p.preset === 'prev_month') return 'за прошлый месяц';
  }
  return `за ${periodLabel(p, now).toLowerCase()}`;
}
